// `FieldMerge` (SuiteImport): one field from another app merged three-way into the project's. `theirs` is what the
// other app has now, `base` what it had at the last import, `ours` the project's field. Changes the other app made
// since the base are applied with the option operations (so they merge with another device's edits); the writer's
// own changes stay; where both changed one thing, ours stays and `conflict` says what.
import type { FieldOption } from '../contract.ts';
import type { MetadataField } from '../model/documents.ts';
import type { FieldSpec, StoreEditor } from '../edit/storeEditor.ts';
import { sortedJSON } from '../review/json.ts';

export const SCRIVENER_FIELDS_KEY = 'scrivenerFields';

type Option = { id: string; name: string; color?: string };
type FieldLike = { id: string; name: string; type: string; options: Option[]; order: number };

export function mergeField(editor: StoreEditor, fields: () => MetadataField[], theirs: FieldSpec, base: MetadataField | FieldLike | undefined, ours: MetadataField | undefined, conflict: (message: string) => void): void {
  if (!ours) { editor.putField(theirs); return; }
  const was: FieldLike = base ?? ours;
  const theirOptions = theirs.options ?? [];
  if (theirs.name !== was.name && ours.name === was.name && theirs.name !== ours.name) {
    try { editor.renameField(ours.id, theirs.name); } catch (e) { conflict(`Couldn’t rename the field to “${theirs.name}”: ${(e as Error).message}`); }
  }
  // A field's type can't change in place here (the Swift's `setFieldType`); Scrivener's custom fields rarely do.
  if (theirs.type !== 'enum' && ours.type !== 'enum') return;

  const baseByID = new Map<string, Option>();
  for (const o of was.options) if (!baseByID.has(o.id)) baseByID.set(o.id, o);
  const theirIDs = new Set(theirOptions.map((o) => o.id));
  let current: Option[] = [...ours.options];

  // Options the other app took out: gone here too, unless the writer changed them since.
  for (const option of was.options) {
    if (theirIDs.has(option.id)) continue;
    const mine = current.find((o) => o.id === option.id);
    if (!mine) continue;
    if (mine.name === option.name && (mine.color ?? null) === (option.color ?? null)) {
      editor.removeOption(ours.id, option.id, undefined, false);
      current = current.filter((o) => o.id !== option.id);
    } else {
      conflict(`“${mine.name}” was removed in the other app and changed in Five Rooms; it's kept.`);
    }
  }

  theirOptions.forEach((option, i) => {
    const mine = current.find((o) => o.id === option.id);
    if (!mine) {
      // New there: added here after the option before it. One the writer removed stays removed.
      if (baseByID.has(option.id)) return;
      let index = 0;
      for (let k = i - 1; k >= 0; k--) {
        const at = current.findIndex((o) => o.id === theirOptions[k].id);
        if (at >= 0) { index = at + 1; break; }
      }
      editor.insertOption(ours.id, option as FieldOption, index);
      current.splice(index, 0, option);
      return;
    }
    const before = baseByID.get(option.id) ?? mine;
    if (option.name !== before.name && option.name !== mine.name) {
      if (mine.name === before.name) editor.renameOption(ours.id, option.id, option.name, undefined, false);
      else conflict(`“${mine.name}” was renamed “${option.name}” in the other app; Five Rooms' name is kept.`);
    }
    if ((option.color ?? null) !== (before.color ?? null) && (option.color ?? null) !== (mine.color ?? null)) {
      if ((mine.color ?? null) === (before.color ?? null)) editor.recolorOption(ours.id, option.id, option.color ?? null);
      else conflict(`“${mine.name}” was given another colour in both apps; Five Rooms' is kept.`);
    }
  });

  // The other app's new order, when the writer hasn't reordered the options both have: theirs fill the places those
  // options hold, and the writer's own options stay where they are.
  const fresh = fields().find((f) => f.id === ours.id)?.options ?? [];
  const shared = new Set(fresh.map((o) => o.id).filter((id) => theirIDs.has(id) && baseByID.has(id)));
  const order = (options: Option[]) => options.map((o) => o.id).filter((id) => shared.has(id));
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, k) => x === b[k]);
  if (same(order(theirOptions), order(was.options)) || !same(order(fresh), order(was.options))) return;
  const queue = order(theirOptions);
  const wanted = fresh.map((o) => (shared.has(o.id) ? queue.shift()! : o.id));
  wanted.forEach((id, index) => editor.moveOption(ours.id, id, index));
}

/** `recordScrivenerFieldBase`: Scrivener's field definitions as the Swift's `[MetadataField]` JSON (sorted keys;
 *  `hidden` only when set, never for Scrivener's). */
export function encodeFieldBase(fields: FieldSpec[]): string {
  return sortedJSON(fields.map((f) => ({
    id: f.id, name: f.name, type: f.type, order: f.order,
    options: (f.options ?? []).map((o) => ({ id: o.id, name: o.name, ...(o.color ? { color: o.color } : {}) })),
  })), (d) => d.toISOString());
}

/** `scrivenerFieldBase`: null when none was kept or it can't be read. */
export function decodeFieldBase(json: unknown): FieldLike[] | null {
  if (typeof json !== 'string') return null;
  try {
    const raw = JSON.parse(json);
    if (!Array.isArray(raw)) return null;
    return raw.flatMap((f): FieldLike[] => {
      if (!f || typeof f !== 'object' || typeof f.id !== 'string' || typeof f.name !== 'string' || typeof f.type !== 'string') return [];
      const options = Array.isArray(f.options)
        ? f.options.flatMap((o: unknown): Option[] => {
          const r = o as Record<string, unknown>;
          return r && typeof r.id === 'string' && typeof r.name === 'string' ? [{ id: r.id, name: r.name, ...(typeof r.color === 'string' ? { color: r.color } : {}) }] : [];
        })
        : [];
      return [{ id: f.id, name: f.name, type: f.type, options, order: typeof f.order === 'number' ? f.order : 0 }];
    });
  } catch {
    return null;
  }
}
